import type { Definition, Organization } from '../../types'
import { InternMap, ScalePower } from 'd3'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { FilterSection, SlippyMap } from './components'
import NavBar from '../NavBar/NavBar'
import { MapToggle } from '../MapTableToggle'
import {
  MobileScreen1,
  Screen2,
  Screen3,
  Screen4,
} from '../ExplainerStepsContainer/components'
import closeIcon from './images/close-icon-orange-mobile.svg'
import { useMeasure } from 'react-use'
import questionMarkIcon from './images/question-mark-icon-small.svg'
import Legend from './Legend'
import MapToggleSwitch from '../MapTableToggle/MapToggleSwitch'

type MapProps = {
  display: string
  setDisplay: Dispatch<SetStateAction<string>>
  handleDisplay: (value: string) => void
  data: InternMap<string, Organization[]>
  rScale: ScalePower<number, number, never>
  definitions: Definition[]
  globalGeoScopeOptions: string[]
  onSelectedGeoScopeOptionsChange: Dispatch<SetStateAction<string[]>>
  globalFocusOptions: string[]
  onSelectedFocusOptionsChange: Dispatch<SetStateAction<string[]>>
  globalStrategyOptions: string[]
  onSelectedStrategyOptionsChange: Dispatch<SetStateAction<string[]>>
  globalLiableInstitution: string
  onLiableInstitutionChange: Dispatch<SetStateAction<string>>
  globalInitCommTask: boolean
  onInitCommTaskChange: (checked: boolean) => void
  globalStageOptions: string[]
  onSelectedStageOptionsChange: Dispatch<SetStateAction<string[]>>
  globalRepairOptions: string[]
  onSelectedRepairOptionsChange: Dispatch<SetStateAction<string[]>>
  globalInitiativeTypeCheckboxes: { [key: string]: boolean }
  onSelectedInitiativeTypeCheckboxesChange: Dispatch<
    SetStateAction<{
      [key: string]: boolean
    }>
  >
  globalInitiativeTypeOptions: string[]
  onSelectedInitiativeTypeOptionsChange: Dispatch<SetStateAction<string[]>>
  onOrganizationClick: (
    organization: Organization | null,
    organizations: Organization[]
  ) => void
  explainerStep: number
  onExplainerStepChange: (step: number) => void
  hasSeenInitialMapExplainer: boolean
  hideInitialMapExplainer: () => void
}

function Map({
  display,
  setDisplay,
  handleDisplay,
  data,
  rScale,
  definitions,
  globalGeoScopeOptions,
  onSelectedGeoScopeOptionsChange,
  globalFocusOptions,
  onSelectedFocusOptionsChange,
  globalStrategyOptions,
  onSelectedStrategyOptionsChange,
  globalLiableInstitution,
  onLiableInstitutionChange,
  globalInitCommTask,
  onInitCommTaskChange,
  globalStageOptions,
  onSelectedStageOptionsChange,
  globalRepairOptions,
  onSelectedRepairOptionsChange,
  globalInitiativeTypeCheckboxes,
  onSelectedInitiativeTypeCheckboxesChange,
  globalInitiativeTypeOptions,
  onSelectedInitiativeTypeOptionsChange,
  onOrganizationClick,
  explainerStep,
  onExplainerStepChange,
  hasSeenInitialMapExplainer,
  hideInitialMapExplainer,
}: MapProps) {
  const toggleRef = useRef<HTMLDivElement | null>(null)
  const svgMapRef = useRef(null)
  const mapWrapperRef = useRef(null)
  const filterSectionRef = useRef<HTMLDivElement | null>(null)
  const [hiddenMapRef, { width: mapWidth, height: mapHeight }] =
    useMeasure<HTMLDivElement>()

  const [showInitialExplainer, setShowInitialExplainer] =
    useState<boolean>(false)
  const handleInitialExplainerVisibility = () => {
    hideInitialMapExplainer()
    setShowInitialExplainer(!showInitialExplainer)
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
    setShowInitialExplainer(true)
    return () => {
      setShowInitialExplainer(false)
    }
  }, [])

  const handleExplainerStepLoad = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
    onExplainerStepChange(1)
  }

  const renderExplainerStepScreen = () => {
    switch (explainerStep) {
      case 1:
        return <Screen2 anchorRef={filterSectionRef} />
      case 2:
        return <Screen3 anchorRef={mapWrapperRef} />
      case 3:
        return <Screen4 anchorRef={toggleRef} />
      default:
        return null
    }
  }
  return (
    <section id="map" className="min-h-screen h-full bg-[#222222] relative">
      {showInitialExplainer && !hasSeenInitialMapExplainer && (
        <div
          className="absolute min-h-screen h-full w-screen z-[1000] overflow-y-hidden"
          role="button"
          onClick={handleInitialExplainerVisibility}
        >
          <MobileScreen1 onClose={handleInitialExplainerVisibility} />
        </div>
      )}
      {explainerStep > 0 && (
        <div
          className="absolute min-h-screen h-full w-full z-[1000] bg-[#222222] bg-opacity-[.78] overflow-y-hidden"
          onClick={() => {
            explainerStep === 3
              ? onExplainerStepChange(0)
              : onExplainerStepChange(explainerStep + 1)
          }}
        >
          <img
            src={closeIcon}
            alt="letter x"
            className="absolute top-0 right-0 p-8 z-[1001]"
            role="button"
            onClick={(evt) => {
              evt.stopPropagation()
              onExplainerStepChange(0)
            }}
          />
          {renderExplainerStepScreen()}
        </div>
      )}
      <div className="w-full px-8 gap-8 flex flex-col min-h-screen h-full">
        <NavBar />
        <div>
          <div className="flex flex-col lg:flex-row justify-left lg:items-center w-full justify-between gap-4">
            <p className="font-decimal text-white text-[26px] whitespace-nowrap">
              Mapping the U.S. <strong>Reparations Movement</strong>
            </p>

            <MapToggleSwitch />
          </div>

          <div className="flex flex-col md:flex-row w-full justify-between items-start">
            <Legend display={display} />
            <img
              src={questionMarkIcon}
              alt="question mark"
              className="flex mt-3 md:-mx-3 md:mt-1"
              role="button"
              onClick={handleExplainerStepLoad}
            />
          </div>
        </div>
        <div ref={mapWrapperRef} className="flex flex-grow min-h-[504px]">
          <SlippyMap
            display={display}
            data={data}
            rScale={rScale}
            onOrganizationClick={onOrganizationClick}
            svgMapRef={svgMapRef}
            hiddenMapRef={hiddenMapRef}
            mapWidth={mapWidth}
            mapHeight={mapHeight}
          >
            <FilterSection
              display={display}
              setDisplay={setDisplay}
              handleDisplay={handleDisplay}
              definitions={definitions}
              selectedGeoScopeOptions={globalGeoScopeOptions}
              onSelectedGeoScopeOptionsChange={onSelectedGeoScopeOptionsChange}
              selectedFocusOptions={globalFocusOptions}
              onSelectedFocusOptionsChange={onSelectedFocusOptionsChange}
              selectedStrategyOptions={globalStrategyOptions}
              onSelectedStrategyOptionsChange={onSelectedStrategyOptionsChange}
              liableInstitution={globalLiableInstitution}
              onLiableInstitutionChange={onLiableInstitutionChange}
              isInitCommTask={globalInitCommTask}
              onInitCommTaskChange={onInitCommTaskChange}
              selectedStageOptions={globalStageOptions}
              onSelectedStageOptionsChange={onSelectedStageOptionsChange}
              selectedRepairOptions={globalRepairOptions}
              onSelectedRepairOptionsChange={onSelectedRepairOptionsChange}
              selectedInitiativeTypeCheckboxes={globalInitiativeTypeCheckboxes}
              onSelectedInitiativeTypeCheckboxesChange={
                onSelectedInitiativeTypeCheckboxesChange
              }
              selectedInitiativeTypeOptions={globalInitiativeTypeOptions}
              onSelectedInitiativeTypeOptionsChange={
                onSelectedInitiativeTypeOptionsChange
              }
              filterSectionRef={filterSectionRef}
              mapHeight={mapHeight}
            />
          </SlippyMap>
        </div>
        <MapToggle toggleRef={toggleRef} />
      </div>
    </section>
  )
}

export default Map
